import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getCopturePrices } from '@vb-django/api/services/drone';

import { Checkout } from '@components/Checkout/Checkout';
import { useCheckout } from '@components/Checkout/context/CheckoutContext';
import { AccountStep } from '@components/Checkout/steps/AccountStep';
import { OrderStep } from '@components/Checkout/steps/OrderStep';
import { PaymentStep } from '@components/Checkout/steps/PaymentStep';
import { ThankYouStep } from '@components/Checkout/steps/ThankYouStep';

import type { OpenCheckoutOptions } from '../../../Checkout.types';
import {
  getDroneOrder,
  hasPersistedOrderPrices,
  saveDroneOrder
} from '../../../utils/orderPersistence';
import { useKlarnaOrderPersistence } from '../../../utils/useKlarnaOrderPersistence';
import { calculateDronePrices } from '../../helpers/helpers';

import { DronePricesData } from '../DroneCheckout.types';

type StepKey = 'order' | 'account' | 'payment' | 'thankyou';

export const DroneCheckoutInner = ({
  stepKeys,
  isOpen,
  onClose,
  openOptions,
  onConsumedOpenOptions
}: {
  stepKeys: StepKey[];
  isOpen?: boolean;
  onClose: () => void;
  openOptions?: OpenCheckoutOptions | null;
  onConsumedOpenOptions?: () => void;
}) => {
  const checkout = useCheckout();
  const { openCheckout, goToStep, isCheckoutOpen } = checkout;
  const [dronePrices, setDronePrices] = useState<DronePricesData | null>(null);

  useKlarnaOrderPersistence({
    checkout,
    stepKeys,
    openOptions,
    variant: 'drone'
  });

  useLayoutEffect(() => {
    if (isOpen) {
      openCheckout();
    }
  }, [isOpen, openCheckout]);

  useEffect(() => {
    if (!openOptions?.step || !isCheckoutOpen) {
      return;
    }
    goToStep(openOptions.step);
    onConsumedOpenOptions?.();
  }, [openOptions?.step, isCheckoutOpen, goToStep, onConsumedOpenOptions]);

  const prevCheckoutOpen = useRef(false);
  useEffect(() => {
    if (prevCheckoutOpen.current && !checkout.isCheckoutOpen && isOpen) {
      onClose();
    }
    prevCheckoutOpen.current = checkout.isCheckoutOpen;
  }, [checkout.isCheckoutOpen, isOpen, onClose]);

  useEffect(() => {
    if (!checkout.isCheckoutOpen) {
      return;
    }

    const fetchDronePrices = async () => {
      const { ok, data } = await getCopturePrices();
      if (ok) {
        setDronePrices(data);
        checkout.setExtraData((prev) => ({ ...prev, dronePrices: data }));
      } else {
        console.error('Failed to fetch drone prices');
      }
    };

    fetchDronePrices();
  }, [checkout.isCheckoutOpen]);

  useEffect(() => {
    if (!checkout.isCheckoutOpen) {
      return;
    }

    const stepKey = stepKeys[checkout.currentStep - 1];
    const pendingPersistedOrder = getDroneOrder();

    if (stepKey === 'thankyou' && hasPersistedOrderPrices(checkout.orderPrices)) {
      return;
    }

    const prices = dronePrices ?? checkout.extraData.dronePrices;

    if (!prices?.products?.length || checkout.selectedProducts.length === 0) {
      if (pendingPersistedOrder || stepKey === 'thankyou') {
        return;
      }
      checkout.setOrderPrices({});
      return;
    }

    const { selectedPackage, selectedForestManagementPlan } = checkout.extraData;

    const priceCalculation = calculateDronePrices({
      properties: checkout.selectedProducts.map((p) => ({
        ref: p.id,
        area: Number(p.area) || 0
      })),
      selectedPackage,
      selectedForestManagementPlan,
      couponDiscount: checkout.couponDiscount,
      dronePrices: prices
    });

    const paymentMethod = priceCalculation.discountedPrice > 20000 ? 'invoice' : 'klarna';

    const nextOrderPrices = {
      discountPrice: priceCalculation.discountedPrice,
      fullPrice: priceCalculation.fullPrice,
      tax: 0.25 * priceCalculation.discountedPrice,
      propertiesHectarePrice: priceCalculation.propertiesHectarePrice,
      propertiesTotalPrice: priceCalculation.propertiesTotalPrice,
      packageHectarePrice: priceCalculation.packageHectarePrice,
      packageTotalPrice: priceCalculation.packageTotalPrice,
      forestManagementPlanTotalPrice: priceCalculation.forestManagementPlanTotalPrice
    };

    checkout.setPaymentMethod(paymentMethod);
    checkout.setOrderPrices(nextOrderPrices);

    if (stepKey === 'payment' && paymentMethod === 'klarna') {
      saveDroneOrder({
        selectedProducts: checkout.selectedProducts,
        orderPrices: nextOrderPrices,
        paymentMethod: 'klarna',
        couponDiscount: checkout.couponDiscount,
        extraData: checkout.extraData
      });
    }
  }, [
    checkout.isCheckoutOpen,
    checkout.currentStep,
    dronePrices,
    checkout.selectedProducts,
    checkout.extraData,
    checkout.couponDiscount,
    stepKeys
  ]);

  const currentStep = stepKeys[checkout.currentStep - 1];

  return (
    <Checkout>
      {currentStep === 'order' && <OrderStep />}
      {currentStep === 'account' && <AccountStep />}
      {currentStep === 'payment' && <PaymentStep />}
      {currentStep === 'thankyou' && <ThankYouStep />}
    </Checkout>
  );
};
